import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import AXToast from '@/components/AXToast';
import { HttpCodeEnum } from '@/config';
import { isWeChat, isAiXueApp, isIOS, getValueFromUserAgent } from '@/utils/ua';

// 白名单code,可能会需要页面内部处理，不要抛错
const whiteCodeList = [HttpCodeEnum.ORDER_EXIST, HttpCodeEnum.REPEAT_PURCHASE];
const whitePathWithNoToast = ['/mweb/course-detail', '/mweb/packageCourseDetail'];

function shouldShowGlobalToast(config?: InternalAxiosRequestConfig): boolean {
  if (config?.skipErrorToast) return false;
  if (whitePathWithNoToast.includes(location.pathname)) return false;
  return true;
}

// 定义 API 响应的基础类型
interface ApiResponse<T = any> {
  code: number;
  msg: string;
  message?: string;
  data: T;
}

// 创建自定义的 request 类型
interface CustomAxiosInstance
  extends Omit<AxiosInstance, 'get' | 'post' | 'put' | 'delete' | 'patch'> {
  get<T = any>(url: string, config?: any): Promise<ApiResponse<T>>;
  post<T = any>(url: string, data?: any, config?: any): Promise<ApiResponse<T>>;
  put<T = any>(url: string, data?: any, config?: any): Promise<ApiResponse<T>>;
  delete<T = any>(url: string, config?: any): Promise<ApiResponse<T>>;
  patch<T = any>(url: string, data?: any, config?: any): Promise<ApiResponse<T>>;
}

const getToken = () => {
  let token = localStorage.getItem('__ax__token');
  // app中token从ua中取
  if (isAiXueApp()) {
    const appToken = getValueFromUserAgent('rememberToken');
    
    if (appToken) {
      localStorage.setItem('__ax__token', appToken);
      token = appToken;
    }
  }
  return token;
};

const initDeviceType = () => {
  if (isAiXueApp()) {
    return isIOS() ? 'IOS' : 'ANDROID';
  }
  return isWeChat() ? 'WXH5' : 'H5';
}

// 创建axios实例
const request = axios.create({
  // baseURL: (import.meta as any).env.VITE_APP_BASE_API || '/backend-api', // 从环境变量获取API基础URL
  timeout: 60000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json',
    DeviceType: initDeviceType(),
    'First-Channel-Id': 'H5',
    'Second-Channel-Id': 'H5',
  },
}) as CustomAxiosInstance;

// 请求拦截器
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 在发送请求之前，获取token
    const token = getToken();
    if (token && config.headers && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 实例默认 Content-Type 为 application/json，会覆盖 FormData；
    // 必须去掉，浏览器/axios 才会自动带 multipart/form-data + boundary（如朗读评测上传）
    if (config.data instanceof FormData && config.headers) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else {
        delete (config.headers as Record<string, unknown>)['Content-Type'];
      }
    }
    return config;
  },
  (error) => {
    // 对请求错误做些什么
    console.error('请求错误:', error);
    return Promise.reject(error);
  },
);

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse) => {
    // 对响应数据做点什么
    const { data } = response;
    // 写成 == 200，原因是next code是'200'，不是200
    if (
      data.code == 200 ||
      data.code == 0 ||
      data.code == '000000' ||
      whiteCodeList.includes(data.code)
    ) {
      return data;
    } else {
      if (shouldShowGlobalToast(response.config)) {
        AXToast.show({
          content: data.mesg || data.msg || data.message || '请求失败',
        });
      }
      const err = new Error(data.mesg || data.msg || data.message || '请求失败');
      (err as Error & { config?: InternalAxiosRequestConfig }).config = response.config;
      (err as Error & { response?: AxiosResponse }).response = response;
      return Promise.reject(err);
    }
  },
  (error) => {
    // 对响应错误做点什么
    console.error('响应错误:', error);
    if (error.response) {
      const { status, data } = error.response;
      if (shouldShowGlobalToast(error.config)) {
        switch (status) {
          case 401:
            AXToast.show(data.mesg || data.msg || data.message || '未授权，请重新登录');
            // 可以在这里处理登录过期逻辑
            localStorage.removeItem('__ax__token');
            // router.push('/login')
            break;
          case 403:
            AXToast.show(data.mesg || data.msg || data.message || '未授权，请重新登录');
            break;
          case 500:
            AXToast.show(data.mesg || data.msg || data.message || '服务异常');
            break;
          default:
            AXToast.show(data.mesg || data.msg || data.message || '请求失败');
        }
      }
    }
    return Promise.reject(error);
  },
);

export default request;
